<template>
  <main>
    <Loader :loader="formLoader" label="Carregando..." loaderType="progress" />

    <section class="md:mt-24 my-5 grid gap-y-3" v-if="!formLoader">
      <p class="text-xl md:text-3xl font-medium">
        {{ isEditable ? "Editar " : "Cadastrar " }}usuário
      </p>

      <form
        @submit.prevent="
          form.role !== 'STUDENT' ? submitUser() : submitUserStudent()
        "
        class="grid grid-cols-12 gap-x-5"
        autocomplete="off"
        v-if="form"
      >
        <div class="flex flex-col my-4 col-span-12 md:col-span-6">
          <p class="mb-2">
            {{ $t("labels.generic", { pt: "Nome completo", en: "Fullname" }) }}
          </p>
          <InputText
            id="name-input"
            type="text"
            v-model="form.name"
            :class="v$.form.name.$error ? 'p-invalid' : ''"
          />
          <Errors :errors="v$.form.name.$errors" />
        </div>
        <div class="flex flex-col my-4 col-span-6 md:col-span-4">
          <p class="mb-2">CPF</p>
          <InputMask
            id="cpf-input"
            mask="999.999.999-99"
            v-model="form.cpf"
            :unmask="true"
            :class="v$.form.cpf.$error ? 'p-invalid' : ''"
          />
          <Errors :errors="v$.form.cpf.$errors" />
        </div>
        <div class="flex flex-col my-4 col-span-6 md:col-span-2">
          <p class="mb-2">
            {{
              $t("labels.generic", {
                pt: "Data de nascimento",
                en: "Birthdate",
              })
            }}
          </p>
          <Calendar
            inputId="birthdate-input"
            v-model="form.birthDate"
            dateFormat="dd/mm/yy"
          />
        </div>
        <div class="flex flex-col my-4 col-span-6 md:col-span-4">
          <p :class="`mb-2 ${isEditable !== '' ? 'text-gray-500' : ''}`">
            E-mail
          </p>
          <InputText
            id="login-input"
            type="text"
            v-model="form.login"
            :class="v$.form.login.$error ? 'p-invalid' : ''"
            :disabled="isEditable !== ''"
          />
          <Errors :errors="v$.form.login.$errors" />
        </div>

        <div
          class="flex flex-col my-4 col-span-6 md:col-span-2"
          v-if="!isEditable && userService.role !== 'APPLICATION_ADMIN'"
        >
          <p
            :class="`mb-2 ${
              service.role == 'ADMINISTRATOR' ? 'text-gray-500' : ''
            }`"
          >
            {{
              $t("labels.generic", { pt: "Tipo de usuário", en: "User type" })
            }}
          </p>
          <Dropdown
            id="role-input"
            v-model="form.role"
            :options="userRole()"
            optionLabel="name"
            optionValue="code"
            placeholder="Selecione o tipo"
            :class="v$.form.role.$error ? 'p-invalid' : ''"
            :disabled="service.role == 'ADMINISTRATOR'"
          />
          <Errors :errors="v$.form.role.$errors" />
        </div>

        <div
          class="flex flex-col my-4 col-span-6 md:col-span-2"
          v-if="form.status !== 'WAITING_ACTIVATION' && isEditable"
        >
          <p class="mb-2">Status</p>
          <Dropdown
            v-model="form.status"
            :options="userStatus"
            optionLabel="name"
            optionValue="code"
            placeholder="Selecione o status"
            :class="v$.form.status.$error ? 'p-invalid' : ''"
          />
          <Errors :errors="v$.form.status.$errors" />
        </div>
        <div
          v-if="
            Boolean(isEditable) ||
            userService.role?.includes('APPLICATION_ADMIN')
          "
          class="flex flex-col my-4 col-span-8 md:col-span-2"
        >
          <p :class="`mb-2 ${Boolean(isEditable) ? 'text-gray-500' : ''}`">
            {{
              $t("labels.generic", { pt: "Organização", en: "Organization" })
            }}
          </p>
          <AutoComplete
            id="organization-input"
            v-model="autocomplete.selected"
            :suggestions="autocomplete.sugestions"
            :forceSelection="true"
            @complete="searchForOrgs($event)"
            placeholder="Selecione a organização"
            optionLabel="name"
            :class="v$.autocomplete.selected.$error ? 'p-invalid' : ''"
            :disabled="
              Boolean(isEditable) || userService.role?.includes('ADMINISTRATOR')
            "
          />
          <Errors :errors="v$.autocomplete.selected.$errors" />
        </div>

        <div v-if="form.role?.includes('STUDENT')" class="mt-5 col-span-12">
          <div
            class="flex justify-between items-center p-4 bg-gray-200 rounded-md"
          >
            <p>
              {{ $t("labels.generic", { pt: "Curso(s)", en: "Course(s)" }) }}
            </p>
            <Button
              label="Adicionar curso"
              icon="pi pi-plus"
              class="btn-simple"
              @click="openModal()"
            />
          </div>
          <div v-if="courses.length == 0" class="mt-5">
            <p class="text-gray-400 font-bold">
              {{
                $t("labels.generic", {
                  pt: "Este usuário ainda não está vinculado a nenhum curso.",
                  en: "This user is not yet linked to any course.",
                })
              }}
            </p>
          </div>
          <div v-if="courses?.length !== 0" class="mt-5">
            <div class="grid grid-cols-12 gap-3">
              <template v-for="(course, i) in courses" :key="i">
                <Card
                  class="col-span-12 md:col-span-3 motion-safe:animate-fadeIn border-b-2 border-b-h-primary-500 overflow-hidden"
                >
                  <template #header>
                    <img
                      :src="course?.file?.url || emptyImg"
                      class="object-cover w-full"
                      style="height: 13rem"
                    />
                  </template>
                  <template #title>
                    <div class="flex justify-between items-center">
                      <p class="truncate max-w-fit">
                        {{ course?.name }}
                      </p>
                      <v-icon
                        name="hi-solid-minus-circle"
                        class="text-h-error-400 cursor-pointer"
                        @click="removeCourse(courses, i)"
                      />
                    </div>
                  </template>
                  <template #subtitle>
                    <p class="text-gray-400 line-clamp-2">
                      {{ course?.description }}
                    </p>
                  </template>
                </Card>
              </template>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3 col-span-12 mt-10">
          <Button
            label="Cancelar"
            class="btn-outline"
            @click="confirmModal()"
          />
          <Button type="submit" label="Salvar" class="btn-primary" />
        </div>
      </form>

      <ConfirmDialog />

      <Dialog
        header="Adicionar curso(s)"
        v-model:visible="displayModal"
        @hide="closeModal()"
        :draggable="false"
        :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
        :style="{ width: '35vw' }"
        class="custom-modal"
        :modal="true"
      >
        <div class="my-3 flex flex-col">
          <p class="mb-2">Curso(s)</p>
          <section
            class="flex items-center gap-3"
            v-for="(item, i) in courseField"
            :key="i"
          >
            <AutoComplete
              id="course-input"
              v-model="courseField[i]"
              :suggestions="autocomplete.sugestionsCourse"
              :forceSelection="true"
              @complete="searchForCourses($event)"
              placeholder="Selecione o curso"
              optionLabel="name"
              :class="`my-2 ${i > 0 ? 'w-11/12' : 'w-full'}`"
            />
            <v-icon
              v-if="i > 0"
              name="hi-solid-minus-circle"
              class="text-h-error-400 cursor-pointer hover:scale-125"
              @click="service.removeCourseField(courseField, i)"
            />
          </section>
        </div>
        <Button
          label="Adicionar curso"
          icon="pi pi-plus"
          @click="service.addCourseField(courseField)"
          class="btn-simple"
        />

        <template #footer>
          <Button label="Cancelar" @click="closeModal()" class="btn-simple" />
          <Button label="Adicionar" @click="addCourse()" class="btn-primary" />
        </template>
      </Dialog>
    </section>
  </main>
</template>

<script lang="ts">
import { email, or, required } from "@/locale/locale.config";
import { Course, Status as StatusCourse } from "@/models/courses.model";
import { Roles, Status, UserForm, UserStudentForm } from "@/models/user.model";
import { UsersRest } from "@/services/rest/users.rest";
import { GoToRoute } from "@/utils/go-to.util";
import HandleError from "@/utils/handle-errors.util";
import { Messages } from "@/utils/messages.util";
import { validateCPF, validateName } from "@/utils/regex.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import useVuelidate from "@vuelidate/core";
import { last, take } from "rxjs";
import { defineComponent } from "vue";
import { UsersService } from "./users.service";

export default defineComponent({
  name: "formUser",
  computed: {
    service(): UsersService {
      return new UsersService();
    },
    rest(): UsersRest {
      return new UsersRest();
    },
    errors(): HandleError {
      return new HandleError();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    userService(): any {
      return getUserFromStorage();
    },
  },
  data() {
    return {
      form: new UserForm(),
      user: new UserForm(),
      userSelected: null as any,
      autocomplete: {
        sugestions: [] as any[],
        sugestionsCourse: [] as any[],
        selected: null as any,
      },
      userTypes: [
        { name: "Administrador", code: Roles.ADMINISTRATOR },
        { name: "Aluno", code: Roles.STUDENT },
        { name: "Professor", code: Roles.TEACHER },
      ] as any,
      userStatus: [
        { name: "Ativado", code: Status.ACTIVATED },
        // { name: "Inativo", code: Status.INACTIVATED },
        { name: "Bloqueado", code: Status.BLOCKED },
      ] as any,
      courses: [] as any[],
      courseField: [] as any[],
      course: null as any,
      displayModal: false as boolean,
      isEditable: this.$route.params.id ?? "",
      hasRole: this.$route.params.role,
      formLoader: false as boolean,
      emptyImg:
        "https://video-thumb.s3.sa-east-1.amazonaws.com/65197bdbplaceholder.png" as string,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        name: {
          required,
          validName: or((value: any) => {
            return validateName(value);
          }),
        },
        cpf: {
          validCPF: or((value: any) => {
            return this.form.cpf ? validateCPF(value) : true;
          }),
        },
        login: { email, required },
        role: this.userService.role?.includes(Roles.APPLICATION_ADMIN)
          ? {}
          : { required },
        status: { required },
      },

      autocomplete: {
        selected: this.userService.role?.includes(Roles.APPLICATION_ADMIN)
          ? { required }
          : {},
      },
    };
  },
  mounted() {
    const roleType: string = String(this.hasRole).toUpperCase();
    if (this.isEditable && roleType != Roles.STUDENT) {
      this.getUserById();
    } else if (roleType == Roles.STUDENT) {
      this.getUserStudentById();
    }
    this.initCourseFields();
  },
  methods: {
    submitUser(): void {
      this.v$.form.$touch();
      this.v$.autocomplete.$touch();
      if (!this.v$.form.$error && !this.v$.autocomplete.$error) {
        var data = new UserForm();
        data = {
          ...this.form,
          role: this.userService.role?.includes(Roles.APPLICATION_ADMIN)
            ? Roles.ADMINISTRATOR
            : this.form.role,
        };
        data.organization = this.autocomplete.selected?.id;
        if (this.isEditable) {
          this.updateUser(data);
        } else {
          this.createUser(data);
        }
      }
    },
    submitUserStudent(): void {
      this.v$.form.$touch();
      this.v$.autocomplete.$touch();
      if (!this.v$.form.$error && !this.v$.autocomplete.$error) {
        var data = new UserStudentForm();
        data.user = this.form;
        data.user.organization = this.autocomplete.selected?.id;
        data.courses = this.courses.map((course) => course.id);

        if (this.isEditable) {
          this.updateUserStudent(data);
        } else {
          this.createUserStudent(data);
        }
      }
    },
    createUser(data: any): void {
      this.formLoader = true;
      this.rest.postUser(data).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.USER_ADDED));
          this.$router.go(-1);
        },
        error: () => {
          this.formLoader = false;
        },
      });
    },
    updateUser(data: any): void {
      const userId: any = this.$route.params.id;
      this.formLoader = true;
      this.rest.putUser(userId, data).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.USER_UPDATED));
          this.$router.go(-1);
        },
        error: () => {
          this.formLoader = false;
        },
      });
    },
    createUserStudent(data: any): void {
      this.formLoader = true;
      this.rest.postUserStudent(data).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.USER_ADDED));
          this.$router.go(-1);
        },
        error: (error) => {
          this.formLoader = false;
        },
      });
    },
    updateUserStudent(data: any): void {
      const userId: any = this.$route.params.id;
      this.formLoader = true;
      this.rest.putUserStudent(userId, data).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.USER_UPDATED));
          this.$router.go(-1);
        },
        error: () => {
          this.formLoader = false;
        },
      });
    },
    getUserById(): void {
      var userRole: any = "";
      const userId: any = this.$route.params.id;
      this.service.user.subscribe({
        next: (response) => {
          var data = response;
          if (response?.birthDate) {
            data.birthDate = new Date(response.birthDate);
          }
          this.form = data;
          this.autocomplete.selected = response?.organization;
          userRole = data.role;
        },
        error: () => {
          this.$router.go(-1);
        },
      });
      this.user.role = userRole;
      this.service.getUser(userId);
    },
    getUserStudentById(): void {
      var userRole: any = "";
      const userId: any = this.$route.params.id;
      this.service.useStudent.subscribe({
        next: (response) => {
          if (response.length === 0) return;
          var user = response?.user;
          var courses = response?.courses;
          if (response.user?.birthDate) {
            user.birthDate = new Date(response.user.birthDate);
          }
          userRole = user?.role;
          this.form = user;
          this.autocomplete.selected = response.user?.organization;
          this.courses = courses;
        },
        error: () => {
          this.$router.go(-1);
        },
      });
      this.user.role = userRole;
      this.service.getUserStudent(userId);
    },
    confirmModal(): void {
      this.$confirm.require({
        message: "Deseja sair da tela de cadastro do usuário?",
        header: `Sair da tela`,
        acceptLabel: "Sair",
        rejectLabel: "Não",
        rejectClass: "btn-simple",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.go.redirectTo("users");
        },
      });
    },
    addCourse(): void {
      this.courseField.map((field) => {
        this.courses.map((course, index) => {
          if (course?.id === field?.id) {
            this.courses.splice(index, 1);
            this.$toast.add(ToastFactory.warn("O curso já foi adicionado!!"));
          }
        });
      });
      var arr = this.courseField.filter(
        (e) => e?.name !== "" && e !== "" && e !== null
      );
      this.courses.push(...arr);
      this.closeModal();
    },
    removeCourse(courses: Course[], index: number): void {
      courses.splice(index, 1);
    },
    initCourseFields(): void {
      this.courseField = new Array(1).fill(null).map(() => new Course());
    },
    openModal(): void {
      this.displayModal = true;
    },
    closeModal(): void {
      this.initCourseFields();
      this.displayModal = false;
    },
    searchForOrgs(event: any): void {
      const { query } = event;
      this.service.orgs.subscribe({
        next: (response) => {
          this.autocomplete.sugestions = response;
        },
      });
      this.service.getOrgs({ page: 0, size: 100, name: query.toLowerCase() });
    },
    searchForCourses(event: any): void {
      const { query } = event;
      this.service.courses.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.autocomplete.sugestionsCourse = response?.content?.filter(
            (e: any) => {
              const isAlready = this.service.isAlreadyExists(
                e,
                this.courseField
              );
              return !isAlready ? e?.name.toLowerCase() : null;
            }
          );
        },
      });
      this.service.getCourses({
        page: 0,
        size: 100,
        name: query.toLowerCase(),
        status: StatusCourse.PUBLISHED.concat(",").concat(
          StatusCourse.NOT_LISTED
        ),
      });
    },
    userRole(): any {
      var newArr: any;
      if (
        this.service.role !== Roles.ADMINISTRATOR &&
        this.service.role !== ""
      ) {
        newArr = this.userTypes.filter(
          (e: any) => e.code !== Roles.ADMINISTRATOR
        );
      } else {
        newArr = this.userTypes;
      }
      return newArr;
    },
  },
});
</script>
